import type { ExecutionModeV1 } from "@adpilot/shared";
import { IconChevronDown } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui/skeleton";
import { DEFAULT_APPROVAL_SEARCH } from "@/lib/approval-search";
import { useFormatters } from "@/lib/format";
import type { ReportingTimeZone } from "@/lib/reporting-time-zone";
import { cn } from "@/lib/utils";
import type { AiRunBreakdown } from "./ai-activity";
import { AiMark, DashboardCard, DashboardCardLink } from "./dashboard-card";
import { formatRunMoment, formatTimeLeft } from "./overview-format";

/** Workspace automation mode; "off" when the AI master switch is paused. */
export type AiAutomationMode = ExecutionModeV1 | "off";

export interface AiStatusCardProps {
  /** Hidden when unknown (policy still loading or not readable for this user). */
  mode: AiAutomationMode | null;
  pendingReviews: number;
  nextReviewExpiresAt: string | null;
  recentRunsIncomplete?: boolean;
  issues: number;
  appliedToday: number;
  autoShareToday: number | null;
  lastRun: AiRunBreakdown | null;
  nextRunAt: string | null;
  /** AI actions executed in the selected reporting period. */
  periodActions: number;
  /** Workspace target ACoS as a ratio (0.24 = 24%); hidden when null. */
  targetAcos: number | null;
  timeZone: ReportingTimeZone;
  now: Date;
  isLoading?: boolean;
  /** Opens the workspace AI policy dialog; the mode label becomes its trigger. */
  onManage?: () => void;
  className?: string;
}

const MODE_KEYS = {
  off: "overview.aiStatus.mode.off",
  recommendation: "overview.aiStatus.mode.recommendation",
  "semi-auto": "overview.aiStatus.mode.semiAuto",
  "full-auto": "overview.aiStatus.mode.fullAuto",
} as const satisfies Record<AiAutomationMode, string>;

const SEGMENTS = [
  { key: "executed", dot: "bg-success" },
  { key: "toReview", dot: "bg-brand" },
  { key: "failed", dot: "bg-destructive" },
  { key: "noChange", dot: "bg-track" },
] as const satisfies ReadonlyArray<{ key: keyof Omit<AiRunBreakdown, "startedAt">; dot: string }>;

interface StatTileProps {
  label: string;
  value: string;
  detail: string;
  alert?: boolean;
  isLoading: boolean;
}

function StatTile({ label, value, detail, alert = false, isLoading }: StatTileProps) {
  return (
    <div className="flex min-w-0 flex-1 basis-0 flex-col gap-0.5 px-3.5 py-3 not-first:border-l not-first:border-line">
      <div className="flex items-center gap-1.5">
        <span className="truncate text-xs font-medium text-text-secondary">{label}</span>
        {alert ? (
          <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-destructive" />
        ) : null}
      </div>
      {isLoading ? (
        <Skeleton className="my-1 h-6 w-10" />
      ) : (
        <span className="text-2xl font-semibold tracking-tight tabular-nums text-ink">{value}</span>
      )}
      <span className="truncate text-badge tabular-nums text-muted-foreground">{detail}</span>
    </div>
  );
}

/** Paper "Card · AdPraxis AI status": review/issue/applied tiles, last-run outcome bar, period footer. */
export function AiStatusCard({
  mode,
  pendingReviews,
  nextReviewExpiresAt,
  recentRunsIncomplete = false,
  issues,
  appliedToday,
  autoShareToday,
  lastRun,
  nextRunAt,
  periodActions,
  targetAcos,
  timeZone,
  now,
  isLoading = false,
  onManage,
  className,
}: AiStatusCardProps) {
  const { t } = useTranslation("dashboard");
  const { locale, formatNumber, formatPercent } = useFormatters();
  const count = (value: number) => formatNumber(value, { maximumFractionDigits: 0 });
  const segments = SEGMENTS.map((segment) => ({ ...segment, value: lastRun?.[segment.key] ?? 0 }));
  const segmentTotal = segments.reduce((sum, segment) => sum + segment.value, 0);
  const legend = segments.map((segment) =>
    t(`overview.aiStatus.segments.${segment.key}`, { count: segment.value }),
  );

  return (
    <DashboardCard aria-labelledby="overview-ai-status-title" className={cn("h-full", className)}>
      <header className="flex min-h-14 items-center justify-between gap-3 px-5 pt-4 pb-3">
        <div className="flex min-w-0 items-center gap-2">
          <AiMark />
          <h2 id="overview-ai-status-title" className="shrink-0 text-base font-semibold text-ink">
            {t("overview.aiStatus.title")}
          </h2>
          {mode && onManage ? (
            <button
              type="button"
              onClick={onManage}
              aria-label={t("overview.aiStatus.manage", { mode: t(MODE_KEYS[mode]) })}
              className="-mx-1 flex min-w-0 items-center gap-0.5 rounded-xs px-1 text-xs text-muted-foreground outline-none transition-colors hover:bg-state-hover hover:text-ink focus-visible:ring-2 focus-visible:ring-focus-ring"
            >
              <span className="truncate">{t(MODE_KEYS[mode])}</span>
              <IconChevronDown className="size-3 shrink-0" stroke={2} aria-hidden />
            </button>
          ) : mode ? (
            <span className="truncate text-xs text-muted-foreground">{t(MODE_KEYS[mode])}</span>
          ) : null}
        </div>
        <DashboardCardLink to="/ai-operations" search={DEFAULT_APPROVAL_SEARCH}>
          {t("overview.aiStatus.operations")}
        </DashboardCardLink>
      </header>

      <div className="flex-1 px-5 pb-5">
        <div className="flex rounded-md border border-line">
          <StatTile
            label={t("overview.aiStatus.toReview")}
            value={count(pendingReviews)}
            detail={
              nextReviewExpiresAt
                ? t("overview.aiStatus.nextExpires", {
                    time: formatTimeLeft(nextReviewExpiresAt, now, t).label,
                  })
                : t("overview.aiStatus.nothingToReview")
            }
            isLoading={isLoading}
          />
          <StatTile
            label={t("overview.aiStatus.issues")}
            value={recentRunsIncomplete ? "—" : count(issues)}
            detail={
              recentRunsIncomplete
                ? t("overview.aiStatus.incomplete")
                : issues > 0
                  ? t("overview.aiStatus.executionFailed")
                  : t("overview.aiStatus.noIssues")
            }
            alert={issues > 0}
            isLoading={isLoading}
          />
          <StatTile
            label={t("overview.aiStatus.appliedToday")}
            value={recentRunsIncomplete ? "—" : count(appliedToday)}
            detail={
              recentRunsIncomplete
                ? t("overview.aiStatus.incomplete")
                : autoShareToday === null
                  ? t("overview.aiStatus.nothingApplied")
                  : t("overview.aiStatus.automaticShare", {
                      share: formatPercent(autoShareToday, 0),
                    })
            }
            isLoading={isLoading}
          />
        </div>
      </div>

      {recentRunsIncomplete ? (
        <div role="status" className="border-t border-line px-5 py-4 text-xs text-muted-foreground">
          {t("overview.aiStatus.incomplete")}
        </div>
      ) : (
        <div className="flex flex-col gap-2.5 border-t border-line px-5 pt-1.5 pb-4">
          <div className="flex items-center justify-between gap-3 text-xs">
            <span className="truncate font-medium text-text-secondary">
              {lastRun
                ? t("overview.aiStatus.lastRun", {
                    time: formatRunMoment(lastRun.startedAt, now, timeZone, locale, t),
                  })
                : t("overview.aiStatus.noRunYet")}
            </span>
            {nextRunAt ? (
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {t("overview.aiStatus.nextRun", {
                  time: formatRunMoment(nextRunAt, now, timeZone, locale, t),
                })}
              </span>
            ) : null}
          </div>
          <div
            role="img"
            aria-label={legend.join(", ")}
            className="flex h-2 gap-0.5 overflow-hidden rounded-[4px]"
          >
            {segmentTotal > 0 ? (
              segments.map((segment) =>
                segment.value > 0 ? (
                  <span
                    key={segment.key}
                    className={cn("h-full basis-0", segment.dot)}
                    style={{ flexGrow: segment.value }}
                  />
                ) : null,
              )
            ) : (
              <span className="h-full flex-1 bg-track" />
            )}
          </div>
          <ul className="flex flex-wrap gap-x-3.5 gap-y-1">
            {segments.map((segment, index) => (
              <li key={segment.key} className="flex items-center gap-1.25">
                <span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", segment.dot)} />
                <span className="text-xs tabular-nums text-text-secondary">{legend[index]}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <footer className="flex items-center justify-between gap-3 border-t border-line bg-table-footer px-5 py-3 text-xs tabular-nums text-text-secondary">
        <span className="truncate">
          {t("overview.aiStatus.periodActions", {
            count: periodActions,
            formatted: count(periodActions),
          })}
        </span>
        {targetAcos !== null ? (
          <span className="shrink-0">
            {t("overview.aiStatus.targetAcos", { value: formatPercent(targetAcos, 0) })}
          </span>
        ) : null}
      </footer>
    </DashboardCard>
  );
}
